import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { PageHero, CTABanner } from "../components/Shared";
import { BLOG_POSTS } from "../lib/blogPosts";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const BlogCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`blog-card-${post.slug}`}
    className="bg-white overflow-hidden group hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl block"
    style={{ border: "1px solid rgba(27,58,107,0.08)" }}
  >
    <div className="h-52 overflow-hidden">
      <img
        src={post.image}
        alt={`${post.title} — cleaning tips from Cindyrella Cleaning, Naples FL`}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3 text-xs" style={{ color: GOLD, fontFamily: FONT_BODY }}>
        <span className="font-bold uppercase tracking-widest">{post.date}</span>
        <span className="flex items-center gap-1" style={{ color: "#9CA3AF" }}>
          <Clock size={12} />
          {post.readTime}
        </span>
      </div>
      <h2 className="text-lg font-bold mb-3 leading-snug" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        {post.title}
      </h2>
      <p className="text-sm leading-relaxed mb-4" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        {post.excerpt}
      </p>
      <span className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-2.5 transition-all" style={{ color: GOLD, fontFamily: FONT_BODY }}>
        Read Article <ArrowRight size={14} />
      </span>
    </div>
  </Link>
);

const fetchPublishedPosts = () =>
  axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/api/blog`)
    .then((res) => (res.data.items?.length ? res.data.items : null))
    .catch(() => null);

export default function BlogPage() {
  const [posts, setPosts] = useState(BLOG_POSTS);

  useEffect(() => {
    fetchPublishedPosts().then((items) => items && setPosts(items));
  }, [setPosts]);

  return (
    <>
      <PageHero
        testid="blog-page-hero"
        badge="Cleaning Tips & Insights"
        title={<>The Cindyrella <span style={{ color: GOLD }}>Blog</span></>}
        subtitle="Expert cleaning advice for Naples homeowners, Airbnb hosts, and business owners."
      />
      <section data-testid="blog-list-section" className="py-16 md:py-24" style={{ background: "#F9FAFB" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {posts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>
      <CTABanner testid="blog-cta-banner" />
    </>
  );
}
